/**
 * accessToolActions — the single data-driven action table for the agent
 * access / sharing tools (teams, share-links, chain-folders, folder
 * permissions).
 *
 * ── WHY one table ──
 * Both flavors of the access surface funnel through the SAME map so the
 * REST contract lives in exactly one place:
 *   • READ actions  → run in the browser as a `client-executed` frontend
 *     tool (registerAccessTools.ts). No approval UI.
 *   • MUTATION actions → gated behind AccessApprovalCard; the card runs
 *     the same `performAccessAction(...)` on Approve.
 *
 * Every entry is `${tool}.${action} → { kind, method, path, body?, pick? }`.
 * `path`/`body` are pure builders over the worker-supplied `payload`;
 * `pick` narrows the API response to what the model needs (default: the
 * whole response object). React-free ⇒ unit-testable.
 *
 * ── CONTRACT NOTE (reconcile with the worker tools) ──
 * The worker (features/agent/tools/*) is the sole authority for tool
 * ADVERTISEMENT + the `payload` shape. This file encodes the exact
 * `payload` keys each action reads (documented per entry). If the worker
 * emits different keys, reconcile HERE — the browser is the executor.
 * Several REST paths here DIVERGE from the first-draft contract handed to
 * this module; each divergence is flagged with `// ⚠ CONTRACT`.
 */
import { request, type RequestOptions } from '@papan/api-client'
// One rule for "can the app already reach this folder", shared with the Settings
// grant flow. Imported from the package entry this feature already uses — a new
// export subpath breaks every dev server that is already running.
import { chainRedundancy } from '@papan/files/client'

export type AccessActionKind = 'read' | 'mutation'

/** Parsed access-request metadata (from a clientTool call's args OR a
 *  mutation question's payload). */
export interface AccessRequestMeta {
  tool: string
  action: string
  payload: Record<string, unknown>
  summary: string
}

/** Result contract — mirrors the worker's ctx.clientTool result shape and
 *  the `_result` side-channel a mutation threads back on Approve. */
export interface AccessResult {
  success: boolean
  data?: Record<string, unknown>
  error?: string
}

interface AccessActionSpec {
  kind: AccessActionKind
  method: 'GET' | 'POST' | 'PATCH' | 'PUT' | 'DELETE'
  /** Build the request path from the payload. */
  path: (p: Record<string, any>) => string
  /** Build the JSON request body (omit for GET/DELETE-with-no-body). */
  body?: (p: Record<string, any>) => unknown
  /** Narrow the response for the model. Default: whole response object. */
  pick?: (res: any) => Record<string, unknown>
  /** Optional follow-up READ that enriches the result the model sees (e.g. "the
   *  app cannot reach this folder yet"). Runs only after a successful call and
   *  must never fail it: a rejection is swallowed — the mutation already
   *  happened, and a retry would repeat it. */
  after?: (res: any, p: Record<string, any>) => Promise<Record<string, unknown>>
}

const seg = (v: unknown) => encodeURIComponent(String(v ?? ''))

/**
 * The action table. Keyed `${tool}.${action}`.
 *
 * Payload keys each action reads are noted inline so the worker agent can
 * emit the matching shape.
 */
export const ACCESS_ACTIONS: Record<string, AccessActionSpec> = {
  // ── team READS moved SERVER-SIDE (issue #386) ────────────────────────
  // team.list/my/members no longer round-trip through the browser — the
  // worker reads them directly via @papan/database's team-access service
  // (features/agent/tools/team.ts). Only team MUTATIONS remain client-
  // executed below (create/add_member/remove_member/set_team_admin), pending
  // the same server-side move for their execution.

  // ── share_link (READ-ish) ────────────────────────────────────────────
  // payload: { fileId, kind? }  (get-or-create is idempotent → read-ish)
  // Keyed on the WIRE name, which stays `get_default` — the tool renamed only
  // its MODEL-facing action (see share-link.ts). `ensure_default` resolves too,
  // via ACTION_ALIASES below, so a worker that does send the new name still
  // works.
  'share_link.get_default': {
    kind: 'read',
    method: 'POST',
    path: () => `/api/share-links/default`,
    body: (p) => ({ fileId: p.fileId, ...(p.kind ? { kind: p.kind } : {}) }),
  },
  // payload: { fileId?, kind?, includeRevoked? }
  'share_link.list': {
    kind: 'read',
    method: 'GET',
    path: (p) => {
      const q = new URLSearchParams()
      if (p.fileId) q.set('fileId', String(p.fileId))
      if (p.kind) q.set('kind', String(p.kind))
      if (p.includeRevoked) q.set('includeRevoked', '1')
      const qs = q.toString()
      // NO trailing slash before the query: the router registers `GET /` under
      // the `/api/share-links` mount, and `/api/share-links/?fileId=…` misses it
      // → a plain-text 404 that surfaced as the opaque "Request failed". The
      // bare `/api/share-links?fileId=…` is the form that matches.
      return `/api/share-links${qs ? `?${qs}` : ''}`
    },
  },

  // ── chain_folder / .chain ─────────────────────────────────────────────
  //
  // KEPT ONLY FOR THE OLD-WORKER DISPATCH. Since #518A the worker raises a
  // consent card and performs the create itself, under the wire action
  // `create_mount` — deliberately a name NO table resolves, so neither an old
  // nor a new bundle POSTs on approval (see chain-folder.ts for why the old
  // bundle is the one that forced this). This `create` entry is only reachable
  // from a PREVIOUS-build worker's `ctx.clientTool` dispatch, which must keep
  // working. Delete it once no deployed worker dispatches here — and do NOT
  // add a `create_mount` entry, which would put the browser back in the write
  // path.
  //
  // The DUPLICATE WRITE it used to carry is closed by that move. A session is
  // multi-attacher, the dispatch is BROADCAST to every attached realm, and the
  // dedup set (`clientToolCallsRun`, clientToolRun.ts) is per PAGE — so two tabs on
  // one chat each executed this create and the drive got two `.chain` rows from
  // one instruction. It was newly reachable rather than pre-existing: until the
  // args-provenance fix both tabs POSTed an empty body and 400'd, so no row was
  // ever created. It was also the ONLY non-idempotent client-executed action
  // here — `manage_access.list` is a read and `share_link.get_default` is
  // idempotent by construction — which is why nothing else in this table needed
  // the same treatment.
  // payload: { targetFileId, parentId?, name? }
  'chain_folder.create': {
    kind: 'read',
    method: 'POST',
    path: () => `/api/files/chain`,
    body: (p) => ({
      targetFileId: p.targetFileId,
      ...(p.parentId !== undefined ? { parentId: p.parentId } : {}),
      ...(p.name ? { name: p.name } : {}),
    }),
  },
  // (NO retarget entry: the API only validates/re-points a chain's target on
  // the rename-to-`.chain` activation path, so the worker tool doesn't offer
  // retarget — re-pointing = delete + create, or the user edits the .chain
  // file in its editor.)

  // ── manage_access (READ) ─────────────────────────────────────────────
  // payload: { folderId }
  // ⚠ CONTRACT: folder permissions live at /api/folders/:folderId/...,
  // NOT /api/orgs/:folderId/... (first-draft contract was wrong).
  'manage_access.list': {
    kind: 'read',
    method: 'GET',
    path: (p) => `/api/folders/${seg(p.folderId)}/permissions`,
  },
  // payload: { folderId } — app grants are composed from the same
  // permissions endpoint (its `apps` + `inheritedApps` arrays); there is
  // no dedicated per-folder app-grant listing.
  'manage_access.app_grants': {
    kind: 'read',
    method: 'GET',
    path: (p) => `/api/folders/${seg(p.folderId)}/permissions`,
    pick: (res) => ({
      apps: res?.apps ?? [],
      inheritedApps: res?.inheritedApps ?? [],
    }),
  },

  // ── manage_access.grant (MUTATION) ───────────────────────────────────
  // payload: { folderId, targetType, targetId, permission, allowSharing? }
  // ⚠ CONTRACT: a TEAM target is wired as targetType:'group' (the API's
  // internal name); the CARD still says "team", never "group".
  'manage_access.grant': {
    kind: 'mutation',
    method: 'POST',
    path: (p) => `/api/folders/${seg(p.folderId)}/permissions`,
    body: (p) => ({
      targetType: p.targetType,
      targetId: p.targetId,
      permission: p.permission,
      ...(p.allowSharing !== undefined ? { allowSharing: p.allowSharing } : {}),
    }),
    // An APP grant is only half the story: the app principal reaches an external
    // folder through a `.chain` in its OWN folder, so a grant alone leaves the
    // app unable to read what the user just approved. Report the chain state and
    // let the model decide to call `chain_folder` — we do NOT create it here:
    // chain writes moved out of the browser (#518A) and placement carries its
    // own consent card.
    after: async (_res, p) => {
      if (p.targetType !== 'app') return {}
      // "No access" is a REMOVAL wearing the grant action's clothes; telling the
      // model to place a chain there would walk the access straight back in.
      if (p.permission === 'hidden' || p.permission === 'noaccess' || p.permission === 'none') return {}
      const appFolderId = String(p.targetId ?? '')
      const folderId = String(p.folderId ?? '')
      if (!appFolderId || !folderId) return {}
      try {
        // "Reachable" is wider than "chained": the app's own folder and a folder
        // inside it need no pointer. A chained PARENT does not count — a chain
        // carries no permission of its own (chain-rules.ts). Same rule the Settings grant flow applies, so the model isn't
        // nudged into creating the redundant chains that flow now skips.
        const [access, crumbs] = await Promise.all([
          request<{ chains?: Array<{ targetFileId?: string | null }> }>(`/api/miniapp/${seg(appFolderId)}/access`),
          request<{ breadcrumbs?: Array<{ id: string }> }>(`/api/files/${seg(folderId)}/breadcrumbs`),
        ])
        const chainedTargetIds = (access.chains ?? []).map((ch) => ch.targetFileId ?? null)
        const { redundant } = chainRedundancy({
          appFolderId,
          targetFolderId: folderId,
          targetAncestorIds: (crumbs.breadcrumbs ?? []).map((c) => c.id),
          chainedTargetIds,
        })
        const chained = chainedTargetIds.includes(folderId)
        if (redundant) return { chained, reachable: true }
        return {
          chained: false,
          reachable: false,
          chainNote:
            'The grant is recorded, but the app has no .chain to this folder (or above it) in its own folder — an app reaches an external folder through a .chain. '
            + 'A chain deeper inside the app may already reach it, capped by the app\'s own level on that chain. Use chain_folder to place one for the full grant.',
        }
      } catch {
        // The chain list sits behind a STRONGER gate than the grant itself
        // (write + modification safeguard on the app), so a caller allowed to
        // grant can still be refused here. Say what we don't know rather than
        // implying the app is fine.
        return {
          reachable: null,
          chainNote:
            'Could not check whether the app can reach this folder. If it is outside the app folder, it needs a .chain (chain_folder) — a chained parent does not count, since a chain carries no permission of its own.',
        }
      }
    },
  },

  // ── team MUTATIONS moved SERVER-SIDE (issue #386) ────────────────────
  // team.create/add_member/remove_member/set_team_admin no longer client-
  // execute. The approval card is consent only; the worker performs the write
  // via @papan/database's team-access service (features/agent/tools/team.ts).
  // AccessApprovalCard treats an action absent from this table as consent-only.

  // ── share_link (MUTATION) ────────────────────────────────────────────
  // payload: { fileId, kind?, recipientGrants?, expiresAt?, appRootFileId?, workspaceId? }
  // ⚠ CONTRACT: there is no generic "create" — it splits into /app and
  // /quick-view by kind (defaults to quick_view).
  'share_link.create': {
    kind: 'mutation',
    method: 'POST',
    path: (p) => `/api/share-links/${p.kind === 'app' ? 'app' : 'quick-view'}`,
    body: (p) => ({
      fileId: p.fileId,
      ...(p.recipientGrants ? { recipientGrants: p.recipientGrants } : {}),
      ...(p.expiresAt ? { expiresAt: p.expiresAt } : {}),
      ...(p.appRootFileId ? { appRootFileId: p.appRootFileId } : {}),
      ...(p.workspaceId ? { workspaceId: p.workspaceId } : {}),
    }),
  },
  // payload: { linkId, id, email, force? } — the worker emits BOTH id keys
  'share_link.send_code': {
    kind: 'mutation',
    method: 'POST',
    // `p.linkId` FIRST: the worker emits `{ linkId, id }` (share-link.ts) while
    // this table historically read only `p.id` — so `seg(undefined)` produced
    // `/api/share-links//send-magic`, a 404 surfaced as the opaque
    // "Request failed". Exactly the bug fixed for `list`, still live here.
    path: (p) => `/api/share-links/${seg(p.linkId ?? p.id)}/send-magic`,
    body: (p) => ({ email: p.email, ...(p.force ? { force: true } : {}) }),
  },
  // payload: { linkId, id } — the worker emits BOTH id keys
  // ⚠ CONTRACT: DELETE /api/share-links/:id (a source-owner reshare kill
  // is the separate POST /:id/owner-revoke — not wired here).
  'share_link.revoke': {
    kind: 'mutation',
    method: 'DELETE',
    path: (p) => `/api/share-links/${seg(p.linkId ?? p.id)}`,
  },

  // ── manage_access.revoke (MUTATION) ──────────────────────────────────
  // payload: { folderId, permId }
  'manage_access.revoke': {
    kind: 'mutation',
    method: 'DELETE',
    path: (p) => `/api/folders/${seg(p.folderId)}/permissions/${seg(p.permId)}`,
  },
}

/**
 * Aliases for renamed model-facing actions. `get_default` reads as a pure read
 * while the action LAZILY MINTS the link, which is why a model batched it in the
 * same step as a `list` that then saw no links (observed on stagetest) — so the
 * MODEL-facing name is now `ensure_default`.
 *
 * The WIRE name deliberately did NOT change (share-link.ts pins it), because the
 * browser half runs in long-lived SPA tabs that deploy separately from the
 * worker: a new worker emitting a new action would fail on every old tab, and no
 * client-side table can fix that direction. This alias covers the reverse — a
 * worker that does emit the new name — so both directions resolve.
 */
const ACTION_ALIASES: Record<string, string> = {
  'share_link.ensure_default': 'share_link.get_default',
}

/** Look up an action spec by `${tool}.${action}`, honouring renames. */
export function getAccessActionSpec(tool: string, action: string): AccessActionSpec | undefined {
  const key = `${tool}.${action}`
  return ACCESS_ACTIONS[key] ?? ACCESS_ACTIONS[ACTION_ALIASES[key] ?? '']
}

/**
 * Execute an access action against the REST API using the user's session
 * (same-origin cookie auth via @papan/api-client). Never throws — maps
 * both unknown-action and API errors into the `{ success:false, error }`
 * contract the worker's ctx.clientTool / question `_result` reads.
 */
export async function performAccessAction(
  tool: string,
  action: string,
  payload: Record<string, unknown> = {},
): Promise<AccessResult> {
  const spec = getAccessActionSpec(tool, action)
  if (!spec) {
    return { success: false, error: `Unknown access action: ${tool}.${action}` }
  }
  try {
    const opts: RequestOptions = { method: spec.method }
    if (spec.body) {
      const b = spec.body(payload)
      if (b !== undefined) opts.body = JSON.stringify(b)
    }
    const res = await request<any>(spec.path(payload), opts)
    let data = spec.pick ? spec.pick(res) : (res as Record<string, unknown>)
    if (spec.after) {
      // Enrichment only. The mutation already landed, so a failed follow-up must
      // not report failure — the model would retry a write that succeeded.
      const extra = await spec.after(res, payload).catch(() => null)
      if (extra) data = { ...data, ...extra }
    }
    return { success: true, data }
  } catch (err) {
    return {
      success: false,
      error: err instanceof Error ? err.message : 'Request failed',
    }
  }
}

// ── Permission-label vocabulary ────────────────────────────────────────
// The canonical folder-ACL labels (mirrors features/files/ui/*AccessDialog
// PERM_LABELS). Never "group" — a team is a "team" in the UI even though
// the wire targetType is 'group'.
export const PERMISSION_LABELS: Record<string, string> = {
  admin: 'Manage & Edit',
  readwrite: 'Read & Write',
  readcopy: 'Read-only',
  read: 'Read-only',
  hidden: 'No access',
  noaccess: 'No access',
  none: 'No access',
  owner: 'Owner',
}

/** Map a raw permission value to its UI label (pass-through if unknown). */
export function permissionLabel(value: unknown): string {
  if (typeof value !== 'string') return value == null ? '' : String(value)
  return PERMISSION_LABELS[value] ?? value
}

/** Map a wire targetType to a human word for the card ("team" not "group"). */
export function targetTypeLabel(t: unknown): string {
  switch (t) {
    case 'group':
      return 'team'
    case 'user':
      return 'person'
    case 'role':
      return 'role'
    case 'app':
      return 'app'
    default:
      return typeof t === 'string' ? t : ''
  }
}
